import { Container, CopyButton, IconButton, Text } from "@fluidattacks/design";
import MDEditor from "@uiw/react-md-editor/nohighlight";
import { encode } from "html-entities";
import { Fragment } from "react";
import type { PluggableList } from "react-markdown/lib";
import rehypeRemoveImages from "rehype-remove-images";
import rehypeSanitize from "rehype-sanitize";
import { useTheme } from "styled-components";
import type { Node } from "unist";
import { visit } from "unist-util-visit";

import { StyledContainer } from "./styled-container";

const removeDangerousAttr = (): ((tree: Node) => void) => {
  return (tree: Node): void => {
    visit(
      tree,
      "element",
      (node: {
        tagName?: string;
        properties?: { href?: string; id?: string };
      }): void => {
        if (node.tagName === "a") {
          // eslint-disable-next-line functional/immutable-data
          node.tagName = "span";
          delete node.properties?.href;
        }
        delete node.properties?.id;
      },
    );
  };
};

const Message = ({
  content,
  role,
}: {
  content: string;
  role: "assistant" | "tool" | "user";
}): JSX.Element => {
  const theme = useTheme();

  return (
    <StyledContainer data-color-mode={"light"}>
      {role === "assistant" ? (
        <Container alignItems={"center"} display={"flex"} gap={0.5} mb={0.5}>
          <IconButton
            height={"24px"}
            icon={"sparkles"}
            iconSize={"sm"}
            style={{
              borderRadius: "50%",
            }}
            variant={"primary"}
            width={"24px"}
          />
          <Text color={theme.palette.gray[800]} fontWeight={"bold"} size={"md"}>
            {"AI Agent"}
          </Text>
        </Container>
      ) : undefined}
      <MDEditor.Markdown
        components={{
          code: ({ children, className }): JSX.Element => {
            if (className !== undefined) {
              return (
                <Fragment>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      overflowX: "auto",
                      position: "absolute",
                      right: "10px",
                      top: "10px",
                    }}
                  >
                    <CopyButton content={children?.toString() ?? ""} />
                  </div>
                  <code className={className}>{children}</code>
                </Fragment>
              );
            }

            return <code>{children}</code>;
          },
        }}
        rehypePlugins={
          [
            rehypeRemoveImages,
            removeDangerousAttr,
            rehypeSanitize({
              tagNames: [
                "h1",
                "h2",
                "h3",
                "h4",
                "h5",
                "h6",
                "p",
                "div",
                "span",
                "code",
                "pre",
                "ul",
                "ol",
                "li",
                "hr",
                "br",
                "em",
                "strong",
                "b",
                "i",
                "u",
                "s",
                "br",
                "table",
                "thead",
                "tbody",
                "tr",
                "td",
                "th",
                "caption",
              ],
            }),
          ] as PluggableList
        }
        source={encode(content)}
        style={{
          backgroundColor:
            role === "user" ? theme.palette.gray[100] : "inherit",
          borderRadius: "12px",
          color: theme.palette.gray[600],
          fontFamily: "Roboto",
          fontSize: "14px",
          marginLeft: role === "user" ? "20px" : "0",
          padding: "16px",
          width: role === "user" ? "calc(100% - 20px)" : "100%",
          wordBreak: "break-word",
        }}
      />
      {role === "assistant" && <CopyButton content={content} />}
    </StyledContainer>
  );
};

export { Message };
